import { computed, Injectable, OnDestroy, signal, Signal, WritableSignal } from '@angular/core';
import { forkJoin, from, map, mergeMap, Observable, Subject, tap } from 'rxjs';
import { Companies, Company } from 'src/app/models/types/Configuration/Settings.type';
import { ProductDetailPageDTO } from 'src/app/models/types/Product/Product.type';
import { AreasWithBatchStock, AreasWithStock, AreaWithBatchStock, AreaWithStock, BatchStockDTO, CompaniesWithBatchStocks, CompaniesWithStoreStocks, CompanyWithBatchStocks, CompanyWithStoreStocks, GlobalAreaDTO, SizesWithStock, StockCounters, StocksDTO, StoreStock } from 'src/app/models/types/Product/Stock.type';
import { StockAndPriceService } from 'src/app/services/api/stock-and-price.service';

@Injectable()
export class PdpService {

  public pdp: WritableSignal<ProductDetailPageDTO | null> = signal(null);

  private _sizeCounters: WritableSignal<SizesWithStock> = signal([]);
  public sizeCounters: Signal<SizesWithStock> = computed(() => this._sizeCounters());

  private readonly _EMPTY_STOCK_COUNTERS: StockCounters = { free: 0, reservation: 0, consignmentIn: 0, inTransit: 0 };

  public initializedGlobalAreaStock: boolean = false;
  public initializedGlobalAreaBatchStock: boolean = false;
  public showGlobaAreaSizes: boolean = false;

  private _globalAreasStock: WritableSignal<AreasWithStock | null> = signal(null);
  public globalAreasStock: Signal<AreasWithStock | null> = computed(() => {
    return this._globalAreasStock()
  });

  private _globalAreasBatchStock: WritableSignal<AreasWithBatchStock | null> = signal(null);
  public globalAreasBatchStock: Signal<AreasWithBatchStock | null> = computed(() => {
    return this._globalAreasBatchStock()
  });

  private _selectedAreaIndex: WritableSignal<number> = signal(-1);
  public selectedAreaIndex: Signal<number> = computed(() => this._selectedAreaIndex());

  public selectedArea: Signal<AreaWithStock | AreaWithBatchStock | null> = computed(() => {
    let selectedIndex = this.selectedAreaIndex();
    let globalAreasStock = this.globalAreasStock();
    let globalAreasBatchStock = this.globalAreasBatchStock();
    if (selectedIndex < 0) {
      return null;
    }
    if (this.isBatchManaged) {
      return globalAreasBatchStock![selectedIndex];
    }
    return globalAreasStock![selectedIndex];
  });

  public loading: Signal<boolean> = computed(() => {
    const area = this.selectedArea();
    if (!area) return false;
    let loadingAreaStock = false;
    let loadingAreaBatchStock = false;
    if (this.isBatchManaged) {
      return (area as AreaWithBatchStock).companiesWithBatchStock?.some(company => company === null);
    }
    return (area as AreaWithStock).companiesWithStock?.some(company => company === null);
    // loadingAreaStock = (area as AreaWithBatchStock).companiesWithBatchStock?.some(company => company === null);
    // loadingAreaBatchStock = (area as AreaWithStock).companiesWithStock?.some(company => company === null);
    // return loadingAreaStock || loadingAreaBatchStock;
  });

  public selectedCompaniesWithStock: Signal<CompaniesWithStoreStocks> = computed(() => {
    let globalAreasStock = this.globalAreasStock();//Per ritriggerare al cambio di global area
    let area = this.selectedArea();
    if (!area) return [];

    if ('companiesWithStock' in area) {
      return [...area.companiesWithStock].filter(company => company !== null);
    }

    return [];
  });

  public selectedCompaniesWithBatchStock: Signal<CompaniesWithBatchStocks> = computed(() => {
    let globalAreasBatchStock = this.globalAreasBatchStock();//Per ritriggerare al cambio di global area
    let area = this.selectedArea();
    if (!area) return [];

    if ('companiesWithBatchStock' in area) {
      return [...area.companiesWithBatchStock].filter(company => company !== null);
    }

    return [];
  });

  public get isSelectedArea(): boolean {
    return this.selectedAreaIndex() >= 0;
  }

  public get isBatchManaged(): boolean {
    return !!this.pdp()?.isBatchManaged;
  }

  public updateSizeCounter$: Subject<string[] | undefined> = new Subject<string[] | undefined>();

  constructor(
    private readonly stockAndPriceService: StockAndPriceService,
  ) { }

  reset() {
    this.pdp.set(null);
    this._sizeCounters.set([]);
    this.initializedGlobalAreaStock = false;
    this.initializedGlobalAreaBatchStock = false;
    this.showGlobaAreaSizes = false;
    this._globalAreasStock.set(null);
    this._globalAreasBatchStock.set(null);
    this._selectedAreaIndex.set(-1);
  }

  setPdp(pdp: ProductDetailPageDTO) {
    this.pdp.set(pdp);
    this.initSizeCounters();
    // this._globalAreasStock.set(null);
    // this._selectedAreaIndex.set(-1);
  }

  initSizeCounters() {
    this._sizeCounters.set(this.pdp()?.sizes?.map(size => ({ ...size, counter: 0 })) || []);
  }

  resetSizeCounters() {
    this._sizeCounters.update(counters => {
      return counters.map(c => ({ ...c, counter: 0 }));
    });
  }

  initGlobalAreaStock(areas: GlobalAreaDTO) {
    if (this.initializedGlobalAreaStock) {
      // this.handleSelectArea(this.selectedAreaIndex(), true);
      return;
    } else {
      this.showGlobaAreaSizes = true;
      this._globalAreasStock.update(val => [...areas.map(area => ({
        ...area, companiesWithStock: new Array(area.companies?.length || 0).fill(null),
      }))] as AreasWithStock);
      this.initializedGlobalAreaStock = true;
    }
  }

  initGlobalAreaBatchStock(areas: GlobalAreaDTO) {
    if (this.initializedGlobalAreaBatchStock) {
      // this.handleSelectArea(this.selectedAreaIndex(), true);
      return;
    } else {
      // this.showGlobaAreaSizes = true;
      this._globalAreasBatchStock.update(val => [...areas.map(area => ({
        ...area, companiesWithBatchStock: new Array(area.companies?.length || 0).fill(null),
      }))] as AreasWithBatchStock);
      this.initializedGlobalAreaBatchStock = true;
    }
  }

  updateGlobalAreaStock_setCompaniesWithStock(areaIndex: number, companies: CompaniesWithStoreStocks) {
    if (this.globalAreasStock()) {
      let globalAreasStock = [...this.globalAreasStock()!];
      globalAreasStock[areaIndex].companiesWithStock = companies;
      this._globalAreasStock.update(val => [...globalAreasStock]);
    }
  }

  updateGlobalAreaBatchStock_setCompaniesWithBatchStock(areaIndex: number, companies: CompaniesWithBatchStocks) {
    if (this.globalAreasBatchStock()) {
      let globalAreasBatchStock = [...this.globalAreasBatchStock()!];
      globalAreasBatchStock[areaIndex].companiesWithBatchStock = companies;
      this._globalAreasBatchStock.update(val => [...globalAreasBatchStock]);
    }
  }

  updateGlobalAreaStock_updateCompanyWithStock(areaIndex: number, companyIndex: number, storeStocks: StocksDTO, avoidCountersUpdate?: boolean) {
    if (this.globalAreasStock()) {
      if (!avoidCountersUpdate) {
        this.updateSizeCounters(storeStocks);
      }
      let globalAreasStock = [...this.globalAreasStock()!];
      let companyStock = { ...globalAreasStock[areaIndex].companiesWithStock[companyIndex] };
      companyStock.storeStocks = [...storeStocks];
      companyStock.stockCounters = this.getStockCounts_CompanyStock({ ...companyStock } as CompanyWithStoreStocks);
      globalAreasStock[areaIndex].companiesWithStock[companyIndex] = companyStock as CompanyWithStoreStocks;
      this._globalAreasStock.update(val => [...globalAreasStock]);
    }
  }

  updateGlobalAreaBatchStock_updateCompanyWithBatchStock(areaIndex: number, companyIndex: number, batchStock: BatchStockDTO) {
    if (this.globalAreasBatchStock()) {
      let globalAreasBatchStock = [...this.globalAreasBatchStock()!];
      let companyStock = { ...globalAreasBatchStock[areaIndex].companiesWithBatchStock[companyIndex] };
      companyStock.batchStocks = [...batchStock];
      companyStock.stockCounters = this.getStockCounts_CompanyBatchStock({ ...companyStock } as CompanyWithBatchStocks);
      globalAreasBatchStock[areaIndex].companiesWithBatchStock[companyIndex] = companyStock as CompanyWithBatchStocks;
      this._globalAreasBatchStock.update(val => [...globalAreasBatchStock]);
    }
  }

  updateGlobalAreaStock_sapCodeChanged() {
    if (this.globalAreasStock() && this.selectedAreaIndex() >= 0) {
      this.globalAreasStock()![this.selectedAreaIndex()].companiesWithStock.forEach((company, index) => {
        this.updateGlobalAreaStock_updateCompanyWithStock(this.selectedAreaIndex(), index, company?.storeStocks || [], true);
      });
    }
  }

  updateGlobalAreaBatchStock_sapCodeChanged() {
    if (this.globalAreasBatchStock() && this.selectedAreaIndex() >= 0) {
      this.globalAreasBatchStock()![this.selectedAreaIndex()].companiesWithBatchStock.forEach((company, index) => {
        this.updateGlobalAreaBatchStock_updateCompanyWithBatchStock(this.selectedAreaIndex(), index, company?.batchStocks || []);
      });
    }
  }

  handleSelectArea(index: number, reloading?: boolean) {
    // this.loading.set(true);
    this._selectedAreaIndex.update(val => index);
    if (index < 0) {
      this.resetSizeCounters();
      return;
    }
    let apis: any[] = [];
    let area = (this.isBatchManaged ? this.globalAreasBatchStock() : this.globalAreasStock())![index];
    const companies = area.companies?.map(c => ({
      code: c.companyCode,
      name: c.companyName,
      country: c.country
    }) as Company) || [];


    if (this.isBatchManaged) {
      //Se ho almeno un null nell'array delle companies con stock allora invoco le API
      if (reloading || (area as AreaWithBatchStock).companiesWithBatchStock.some(company => company === null)) {
        apis.push(this.loadBatchStock_GlobalHQ_asObservable(companies, index));
      } else {
        // this.updateSizeCounters_forArea(area);
      }
    } else {
      //Se ho almeno un null nell'array delle companies con stock allora invoco le API
      if (reloading || (area as AreaWithStock).companiesWithStock.some(company => company === null)) {
        apis.push(this.loadStock_GlobalHQ_asObservable(companies, index));
      } else {
        this.updateSizeCounters_forArea(area as AreaWithStock);
      }
    }

    forkJoin(apis).subscribe({
      complete: () => {
        // this.loading.set(false);
      },
      error: (err) => {
        // this.loading.set(false);
        console.log("Errore nel caricamento", err);
      }
    });

  }

  loadStock_GlobalHQ_asObservable(companies: Companies, areaIndex: number): Observable<any> {
    const sapCode = this.pdp()!.sapCode;
    const catalogCode = this.pdp()!.catalogCode;
    //Setto l'array di null per triggerare i loader sulle company
    let companiesWithStoreStocks: CompaniesWithStoreStocks = new Array(companies.length).fill(null);
    return from(companies).pipe(
      mergeMap((company, index) => {
        companiesWithStoreStocks[index] = {
          ...company,
          storeStocks: undefined,
          stockCounters: this._EMPTY_STOCK_COUNTERS
        };
        return this.stockAndPriceService.getProductStock_HQ(sapCode, catalogCode, company.code).pipe(
          map(res => ({ res, index }))
        );
      }),
      tap(() => {
        this.updateGlobalAreaStock_setCompaniesWithStock(areaIndex, companiesWithStoreStocks);
      }),
      tap(({ res, index }) => {
        this.updateGlobalAreaStock_updateCompanyWithStock(areaIndex, index, res);
      })
    );
  }

  loadBatchStock_GlobalHQ_asObservable(companies: Companies, areaIndex: number): Observable<any> {
    const catalogCode = this.pdp()!.catalogCode;
    //Setto l'array di null per triggerare i loader sulle company
    let companiesWithBatchStocks: CompaniesWithBatchStocks = new Array(companies.length).fill(null);
    return from(companies).pipe(
      mergeMap((company, index) => {
        companiesWithBatchStocks[index] = {
          ...company,
          batchStocks: undefined,
          stockCounters: this._EMPTY_STOCK_COUNTERS
        };
        return this.stockAndPriceService.getBatchProductStock_HQ(catalogCode, company.code).pipe(
          map(res => ({ res, index }))
        );
      }),
      tap(() => {
        this.updateGlobalAreaBatchStock_setCompaniesWithBatchStock(areaIndex, companiesWithBatchStocks);
      }),
      tap(({ res, index }) => {
        this.updateGlobalAreaBatchStock_updateCompanyWithBatchStock(areaIndex, index, res);
      })
    );
  }

  getStockCounts_CompanyStock(companyStock: CompanyWithStoreStocks): StockCounters {
    let { free, reservation, consignmentIn, inTransit } = { ...this._EMPTY_STOCK_COUNTERS };
    if (companyStock.storeStocks) {
      let index = companyStock.storeStocks.findIndex(store => store.sapcode === this.pdp()?.sapCode);
      if (index >= 0) {
        companyStock.storeStocks[index].storeStocks?.forEach(stock => {
          const counters = this.stockAndPriceService.getStockCounters(stock);
          free += counters.free;
          reservation += counters.reservation;
          consignmentIn += counters.consignmentIn;
          inTransit += counters.inTransit;
        })
      }
    }
    return { free, reservation, consignmentIn, inTransit };
  }

  getStockCounts_CompanyBatchStock(companyStock: CompanyWithBatchStocks): StockCounters {
    let { free, reservation, consignmentIn, inTransit } = { ...this._EMPTY_STOCK_COUNTERS };
    if (companyStock.batchStocks) {
      companyStock.batchStocks.forEach(stock => {
        const counters = this.stockAndPriceService.getStockCounters(stock.storeStocks);
        free += counters.free;
        reservation += counters.reservation;
        consignmentIn += counters.consignmentIn;
        inTransit += counters.inTransit;
      })

    }
    return { free, reservation, consignmentIn, inTransit };
  }

  updateSizeCounters_forArea(area: AreaWithStock) {
    area.companiesWithStock.forEach(storeStocks => {
      if (storeStocks && storeStocks.storeStocks) {
        this.updateSizeCounters(storeStocks.storeStocks)
      }
    });
  }


  updateSizeCounters_forCompany(company: CompanyWithStoreStocks | null) {
    if (company && company.storeStocks) {
      if (company.storeStocks.length === 0) {
        this.resetSizeCounters();
      } else {
        this.resetSizeCounters();
        this.updateSizeCounters(company.storeStocks);
      }
    } else {
      this.updateSizeCounters_forArea(this.globalAreasStock()![this.selectedAreaIndex()]);
    }
  }

  updateSizeCounters_forStore(storeStocks: StocksDTO | null, store: string) {
    if (storeStocks) {
      if (storeStocks.length === 0) {
        this.resetSizeCounters();
      } else {
        this.resetSizeCounters();
        // this.updateSizeCounters(storeStocks);
        storeStocks.forEach(stocks => {
          let hasStockInThisStore = stocks.storeStocks?.some(s => s.store === store);
          let sapCode = stocks.sapcode;
          if (hasStockInThisStore) {
            this.updateSizeCountersBySapCode(sapCode);
          }
        })
      }
    }
  }

  updateSizeCounters(storeStocks: StocksDTO) {
    storeStocks.forEach(storeStock => {
      let sizeCounterIndex = this.sizeCounters().findIndex(size => size.sapCode === storeStock.sapcode);
      if (this.sizeCounters()[sizeCounterIndex]) {
        this._sizeCounters.update(sizes => {
          sizes[sizeCounterIndex].counter += 1;
          return sizes;
        })
      }
    })
  }

  updateSizeCountersBySapCode(sapCode: string) {
    let sizeCounterIndex = this.sizeCounters().findIndex(size => size.sapCode === sapCode);
    if (this.sizeCounters()[sizeCounterIndex]) {
      this._sizeCounters.update(sizes => {
        sizes[sizeCounterIndex].counter += 1;
        return sizes;
      })
    }
  }

  updateSizeCountersForHqUser(company: CompanyWithStoreStocks | null, storeStock?: StoreStock | null) {
    switch (true) {
      case (company === null && storeStock === undefined): {
        this.updateSizeCounter$.next(undefined);
        break;
      }
      case (company && !storeStock): {
        this.updateSizeCounter$.next(company.storeStocks?.map(s => s.sapcode) || []);
        break;
      }
      case (!!company && !!storeStock): {
        let sapCodes: string[] = [];
        company?.storeStocks?.forEach(stocks => {
          let hasStockInThisStore = stocks.storeStocks?.some(s => s.store === storeStock?.store);
          let sapCode = stocks.sapcode;
          if (hasStockInThisStore) {
            sapCodes.push(sapCode);
          }
        });
        this.updateSizeCounter$.next(sapCodes);
        break;
      }
    }
  }

}
